import { useState } from 'react'

import { useGetCompanies } from '@apps-common/apis/portal/company'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { ControlledInputDebounced } from '@apps-common/components/elements/form'
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { NoResultsCard } from '../common'

const isSearchLengthValid = (value: string) => !!value && value.length >= 3

export interface ICompanySelectModalProps {
  subtitle: string
  title: string
  companyType: string
}

export const CompanySelectModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: ICompanySelectModalProps
}) => {
  const { subtitle, title, companyType } = componentProps
  const [search, setSearch] = useState<string>('')

  const {
    data: companies,
    isLoading: companiesIsLoading,
    isFetching: companiesIsFetching,
    isRefetching: companiesIsRefetching,
    isError: companiesIsError
  } = useGetCompanies({
    config: {
      enabled: isSearchLengthValid(search)
    },
    name: search,
    type: companyType
  })

  const loading = companiesIsLoading || companiesIsFetching || companiesIsRefetching || companiesIsError

  return (
    <ModalContainer alwaysMobile>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle={subtitle} title={title} />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
        <div className='relative flex items-center justify-start'>
          <ControlledInputDebounced
            id='company-option-search'
            type='text'
            containerClassName='mt-[20px] w-full'
            label='Search'
            placeholder='Start typing company name'
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
          />
          {search?.length > 0 && (
            <button
              type='button'
              aria-label='Clear search'
              className='absolute mt-[2px] right-[30px] sm:right-[10px] text-[20px] text-text'
              onClick={() => setSearch('')}
            >
              <commonIcons.close />
            </button>
          )}
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[160px]'>
        <div className='grid gap-[10px]'>
          {!loading && !isSearchLengthValid(search) && <NoResultsCard>Start typing above to search.</NoResultsCard>}
          {!loading && isSearchLengthValid(search) && companies?.length === 0 && (
            <NoResultsCard>No results for {`"${search}"`}</NoResultsCard>
          )}
          {loading &&
            Array.from(Array(12).keys()).map((k) => (
              <div
                key={`company-option-select-skeleton-${k}`}
                className='w-full flex items-center justify-between px-[20px] bg-surface border border-border animate-pulse rounded-xl h-[64px] gap-6'
              >
                <div className='w-[60%] bg-surface-2 h-6 rounded-xl' />
                <div className='bg-surface-2 h-[30px] w-[30px] rounded-full' />
              </div>
            ))}
          {!loading &&
            isSearchLengthValid(search) &&
            companies?.map((c) => (
              <button
                key={c.companyId}
                aria-label={`company-option-select-${c.companyId}`}
                type='button'
                className='w-full flex items-center justify-between px-[20px] bg-surface border border-border text-text jost-light rounded-xl h-[64px]'
                onClick={() => onDismiss(JSON.stringify(c), ModalDismiss.Confirm)}
              >
                {c.companyName}
                <ButtonRoundSm type='div' className='bg-brand text-on-brand'>
                  <commonIcons.arrowAngled className='text-[1.45em]' />
                </ButtonRoundSm>
              </button>
            ))}
        </div>
      </ModalBody>
    </ModalContainer>
  )
}
